import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Segmented } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useProjects } from "../../queries/projects";
import { useClients } from "../../queries/clients";
import { ClientProps, ProjectProps, ProjectStatus } from "../../types/interfaces";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { ProjectStatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import AuthContext from "../../context/AuthContext";
import styles from "./projects.module.css";

const ORDER: ProjectStatus[] = ["active", "on_hold", "completed", "archived"];
const GROUP_LABEL: Record<ProjectStatus, string> = {
  active: "Active", on_hold: "On hold", completed: "Completed", archived: "Archived",
};

/**
 * Projects is a card grid, not a table — deliberately. It's the one list you
 * browse and pick from rather than scan and sort, so it should not look like
 * Clients, Users or the backlog.
 */
export const ProjectListPage = () => {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isStaff = userData?.role === "admin" || userData?.role === "user";
  const { data: projects, isLoading, isError, error } = useProjects();
  const { data: clients } = useClients({ enabled: isStaff });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"open" | "all">("open");

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (projects || [])
      .filter((p: ProjectProps) => (filter === "open" ? p.status === "active" || p.status === "on_hold" : true))
      .filter((p: ProjectProps) => (q ? p.name.toLowerCase().includes(q) : true));
  }, [projects, query, filter]);

  const groups = ORDER.map((status) => ({ status, items: rows.filter((p: ProjectProps) => p.status === status) }))
    .filter((g) => g.items.length > 0);

  const clientName = (id: string) => (clients || []).find((c: ClientProps) => c._id === id)?.name;

  return (
    <PageShell
      kind="list"
      kicker="Delivery"
      title="Projects"
      subtitle="Every engagement we're running, grouped by where it stands."
    >
      <Toolbar count={rows.length} unit="projects">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--q-text-faint)" }} />}
          placeholder="Search projects"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
        <Segmented
          value={filter}
          onChange={(v) => setFilter(v as "open" | "all")}
          options={[{ label: "Open", value: "open" }, { label: "All", value: "all" }]}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        {groups.length === 0 ? (
          <EmptyState
            title="No projects match"
            description="Clear the search, or switch to All to include completed and archived work."
          />
        ) : (
          groups.map(({ status, items }) => (
            <section key={status}>
              <div className={styles.groupHead}>
                <span className={styles.groupTitle}>{GROUP_LABEL[status]}</span>
                <span className={styles.groupCount}>{items.length}</span>
              </div>
              <div className={styles.grid}>
                {items.map((p: ProjectProps) => (
                  <button
                    key={p._id}
                    type="button"
                    className={styles.card}
                    onClick={() => navigate(`/projects/${p._id}/board`)}
                  >
                    <span className={styles.name}>{p.name}</span>
                    <p className={styles.desc}>{p.description || "No description yet."}</p>
                    <span className={styles.foot}>
                      <ProjectStatusTag status={p.status} />
                      <span className={styles.client}>{clientName(p.clientId)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ))
        )}
      </DisplayComponent>
    </PageShell>
  );
};
